import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Rocket, Globe, FileText, Filter, Calendar, Workflow, Settings2,
  CreditCard, MessageSquare, BarChart3, Sparkles, ArrowRight, Crown,
  CheckCircle2, Layers,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div
      ref={ref}
      className={`incubadora-reveal ${isVisible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

const buildBlocks = [
  { icon: <Globe className="w-6 h-6" />, title: "Web corporativa", desc: "Tu casa digital, diseñada para convertir." },
  { icon: <FileText className="w-6 h-6" />, title: "Landing page", desc: "La página de aterrizaje de tu campaña." },
  { icon: <Filter className="w-6 h-6" />, title: "Embudo completo", desc: "Campaña + recorrido del cliente de principio a fin." },
  { icon: <Calendar className="w-6 h-6" />, title: "Sistema de reservas", desc: "Tus clientes agendan solos." },
  { icon: <Workflow className="w-6 h-6" />, title: "Automatizaciones comerciales", desc: "Seguimiento de leads sin esfuerzo manual." },
  { icon: <Settings2 className="w-6 h-6" />, title: "Automatizaciones operativas", desc: "Tu operativa interna, eficiente y automática." },
  { icon: <CreditCard className="w-6 h-6" />, title: "Pasarela de pagos", desc: "Cobra online desde el día uno." },
  { icon: <MessageSquare className="w-6 h-6" />, title: "Comunicaciones transaccionales", desc: "Correos y WhatsApps automáticos con variables." },
  { icon: <BarChart3 className="w-6 h-6" />, title: "Medición completa", desc: "Search Console, GA4 y todo lo necesario para saber qué funciona." },
];

const priceTable = [
  { plan: "Basic", normal: "249€/mes", discount: "199€/mes" },
  { plan: "Pro", normal: "590€/mes", discount: "472€/mes", highlighted: true },
  { plan: "4 servicios", normal: "1.290€/mes", discount: "1.032€/mes" },
];

const processSteps = [
  { n: "01", title: "Contratas la Incubadora", desc: "Pago único de 5.900€ (o 3 plazos con Klarna)." },
  { n: "02", title: "Sesión de arranque", desc: "Entendemos tu negocio, tu operativa y tus objetivos." },
  { n: "03", title: "Montamos el sistema", desc: "Web, landing, embudo, automatizaciones, pagos y medición." },
  { n: "04", title: "Conectamos y orquestamos", desc: "Todas las piezas funcionando entre sí." },
  { n: "05", title: "Entrega y activación", desc: "Tu negocio digital completo, funcionando en 90 días." },
];

const IncubadoraDemo = () => {
  return (
    <div className="space-y-20 md:space-y-36 overflow-hidden">
      {/* ━━━ HERO ━━━ */}
      <ServiceHero
        eyebrow="Servicio puntual premium · Pago único"
        title={<>Incubadora de <Highlight>Proyecto</Highlight></>}
        description={
          <>
            Tu negocio digital montado al completo, desde cero y bien hecho. Un
            sistema entero conectado y orquestado con las mejores herramientas
            del mercado: web, embudo, automatizaciones, pagos y medición.{" "}
            <strong className="text-foreground font-semibold">
              Todo funcionando como una sola máquina.
            </strong>
          </>
        }
        includes={["5.900€ pago único · IVA incluido", "Entrega en 90 días", "Financiable en 3 plazos con Klarna"]}
        primaryCta={{ label: "Solicitar mi Incubadora", href: "/#contact" }}
        secondaryCta={{ label: "Ver qué incluye", href: "#incluye" }}
        decoration={
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-xl shadow-primary/30 flex items-center justify-center"
              style={{ animation: "float 6s ease-in-out infinite" }}
            >
              <Rocket className="w-8 h-8" />
            </div>
            <div className="text-sm text-muted-foreground font-body">
              El producto más completo del catálogo
            </div>
          </div>
        }
      />

      {/* ━━━ INCLUYE / NO INCLUYE ━━━ */}
      <div id="incluye">
        <IncludesExcludes
          includes={[
            "<strong>1 web corporativa</strong>",
            "<strong>1 landing page</strong>",
            "<strong>1 campaña con embudo completo</strong>",
            "<strong>Sistema de reservas</strong>",
            "<strong>Automatizaciones comerciales y operativas</strong>",
            "<strong>Pasarela de pagos</strong>",
            "<strong>Correos y WhatsApps transaccionales con variables</strong>",
            "<strong>Sistemas de medición completos</strong> (Search Console, GA4, todo)",
            "Todo <strong>conectado y orquestado entre sí</strong> para máxima automatización y eficiencia operativa",
            "<strong>20% de descuento VITALICIO en todos los planes mensuales</strong> (para siempre, aunque canceles y vuelvas)",
          ]}
          excludes={[
            "El trabajo mensual recurrente (Redes, SEO, gestión de Ads… eso son los planes mensuales)",
            "Inversión publicitaria (la pagas tú directamente a las plataformas)",
            "Licencias de herramientas de terceros (las paga el cliente)",
          ]}
        />
      </div>

      {/* ━━━ PARA QUIÉN ES ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-primary/[0.10] via-primary/[0.04] to-transparent shadow-2xl shadow-primary/10">
            <CardContent className="p-6 md:p-16">
              <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl md:rounded-3xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center shrink-0 shadow-xl shadow-primary/30">
                  <Layers className="w-8 h-8 md:w-10 md:h-10" />
                </div>
                <div>
                  <Badge variant="secondary" className="mb-4">Para quién es</Badge>
                  <h3 className="text-3xl md:text-4xl font-bold mb-5 leading-tight">
                    Para quien quiere empezar bien desde el principio
                  </h3>
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                    La Incubadora es para ti si estás empezando de cero, o si tu
                    negocio ya funciona pero su estructura digital es un caos y
                    quieres rehacerla bien de una vez. En 90 días te entregamos
                    un sistema completo: no piezas sueltas, sino un engranaje
                    donde tu web, tu embudo, tus reservas, tus pagos y tus
                    automatizaciones trabajan juntos sin que tengas que tocar
                    nada.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ QUÉ MONTAMOS ━━━ */}
      <section className="max-w-6xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-10 md:mb-14">
            <Badge variant="secondary" className="mb-4">Qué montamos exactamente</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              9 piezas. Un solo sistema.
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Cada pieza con la mejor herramienta del mercado. Todas conectadas
              y trabajando juntas.
            </p>
          </div>
        </AnimateIn>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {buildBlocks.map((block, i) => (
            <AnimateIn key={block.title} delay={i * 0.05}>
              <div className="group rounded-2xl border border-border bg-card p-7 h-full hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {block.icon}
                </div>
                <h4 className="font-bold text-lg mb-2">{block.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{block.desc}</p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ BENEFICIO VITALICIO 20% ━━━ */}
      <section className="max-w-6xl mx-auto px-4">
        <AnimateIn>
          <div className="relative">
            <div className="absolute -inset-12 bg-gradient-to-br from-primary/30 via-primary/10 to-transparent blur-3xl rounded-[3rem] pointer-events-none" />

            <Card className="relative overflow-hidden border-2 border-primary/40 bg-gradient-to-br from-background via-primary/[0.04] to-primary/[0.10] shadow-2xl shadow-primary/20">
              <CardContent className="p-6 md:p-16">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest mb-6 shadow-lg shadow-primary/30">
                    <Crown className="w-4 h-4" />
                    Beneficio exclusivo
                  </div>
                  <h2 className="text-4xl md:text-7xl font-bold mb-4 bg-gradient-to-br from-foreground via-primary to-primary/70 bg-clip-text text-transparent leading-tight">
                    20% de descuento<br />para siempre
                  </h2>
                  <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
                    Quien pasa por la Incubadora se convierte en cliente con
                    condiciones especiales <strong className="text-foreground">de por vida</strong>:
                    un 20% de descuento permanente en todos nuestros planes
                    mensuales. Para siempre. Aunque canceles y vuelvas años después,
                    tu descuento te estará esperando.
                  </p>
                </div>

                {/* Tabla comparativa */}
                <div className="grid md:grid-cols-3 gap-4 mt-12">
                  {priceTable.map((row) => (
                    <div
                      key={row.plan}
                      className={`relative rounded-2xl p-7 transition-all ${
                        row.highlighted
                          ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-xl shadow-primary/30 md:-translate-y-3"
                          : "bg-card border border-border"
                      }`}
                    >
                      <div className={`text-xs uppercase tracking-widest mb-3 ${row.highlighted ? "opacity-80" : "text-muted-foreground"}`}>
                        Plan {row.plan}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className={`text-xs mb-1 ${row.highlighted ? "opacity-70" : "text-muted-foreground"}`}>Precio normal</div>
                          <div className={`text-lg line-through ${row.highlighted ? "opacity-60" : "text-muted-foreground"}`}>
                            {row.normal}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs mb-1 ${row.highlighted ? "opacity-90" : "text-primary font-semibold"}`}>
                            Con Incubadora
                          </div>
                          <div className="text-3xl font-bold font-display">
                            {row.discount}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ PROCESO 90 DÍAS ━━━ */}
      <section className="max-w-6xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-10 md:mb-14">
            <Badge variant="secondary" className="mb-4">Plazo de entrega: 90 días</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Cómo lo montamos</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              5 fases. 90 días. Tu negocio digital al completo.
            </p>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-5 gap-4">
          {processSteps.map((step, i) => (
            <AnimateIn key={step.n} delay={i * 0.08}>
              <div className="rounded-2xl border border-border bg-card p-6 h-full hover:border-primary/40 transition-colors">
                <div className="font-display text-4xl bg-gradient-to-br from-primary to-primary/60 bg-clip-text text-transparent mb-3">
                  {step.n}
                </div>
                <h4 className="font-bold text-sm mb-2">{step.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ CTA FINAL ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <Card className="border-2 border-primary/30 overflow-hidden relative shadow-2xl shadow-primary/10">
            <div className="absolute -inset-10 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent rounded-[3rem] blur-3xl pointer-events-none" />
            <CardContent className="p-6 md:p-20 text-center relative z-10">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl md:rounded-3xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center mx-auto mb-6 md:mb-8 shadow-xl shadow-primary/30">
                <Rocket className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">
                ¿Listo para montar tu negocio digital de verdad?
              </h2>
              <p className="text-muted-foreground text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
                5.900€, financiable en 3 plazos. En 90 días lo tienes todo
                funcionando. Y un{" "}
                <strong className="text-foreground">
                  20% de descuento de por vida
                </strong>{" "}
                en lo que venga después.
              </p>
              <Link
                to="/#contact"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 sm:px-10 py-4 sm:py-5 rounded-full bg-primary text-primary-foreground font-medium text-base hover:shadow-glow transition-all"
              >
                Solicitar mi Incubadora
                <ArrowRight className="w-5 h-5" />
              </Link>
              <div className="mt-6 flex items-start justify-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                Pago único · IVA incluido · Financiable en 3 plazos con Klarna
              </div>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>
    </div>
  );
};

export default IncubadoraDemo;
